import { useLayoutEffect } from 'react';
import { Grid, Image } from 'semantic-ui-react';
import ReactPlayer from 'react-player/lazy';
import FadeInSection from '../components/FadeInSection';
import { motion } from 'framer-motion';
import lily from '../assets/Lily01.webp';
import car from '../assets/car.png';
import rs from '../assets/rs.png';
import wcr from '../assets/wcr.png';
import sfar from '../assets/sfar.jpeg';
import green from '../assets/green-logo.jpg';
import gold from '../assets/gold.png';
import { Helmet } from 'react-helmet-async';
import '../styles/About.scss';

const logosContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const logoItem = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

function About() {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Helmet>
        <title>About Lily | Lily Lai Real Estate</title>
        <meta name="description" content="Get to know Lily Lai — a passionate Bay Area Realtor with deep market knowledge, expertise in home renovations, and a commitment to guiding clients with care and clarity." />
        <link rel="canonical" href="https://www.lilylaisf.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lilylaisf.com/about" />
        <meta property="og:title" content="About Lily | Lily Lai Real Estate" />
        <meta property="og:description" content="A passionate Bay Area Realtor with deep market knowledge and a commitment to guiding clients with care and clarity." />
        <meta property="og:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/xmctef45wz3wwv1bxvwb" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Lily | Lily Lai Real Estate" />
        <meta name="twitter:description" content="A passionate Bay Area Realtor with deep market knowledge and a commitment to guiding clients with care and clarity." />
        <meta name="twitter:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/xmctef45wz3wwv1bxvwb" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Lily Lai",
          "jobTitle": "Licensed Real Estate Agent",
          "url": "https://www.lilylaisf.com/about",
          "image": "https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/xmctef45wz3wwv1bxvwb",
          "telephone": "+14157282623",
          "email": "lily@lilylaisf.com",
          "worksFor": {
            "@type": "Organization",
            "name": "The Lurie Group at City Real Estate",
            "url": "https://cityrealestatesf.com/"
          },
          "memberOf": [
            { "@type": "Organization", "name": "National Association of Realtors" },
            { "@type": "Organization", "name": "San Francisco Association of Realtors" },
            { "@type": "Organization", "name": "Women's Council of Realtors" }
          ],
          "knowsAbout": [
            "San Francisco Real Estate",
            "Bay Area Home Buying",
            "Home Selling Strategy",
            "San Francisco Neighborhoods",
            "Home Renovation",
            "Chinese-language Real Estate Services",
            "Pacific Heights real estate",
            "Marina District real estate",
            "Noe Valley real estate",
            "Peninsula real estate"
          ],
          "knowsLanguage": ["English", "Cantonese", "Mandarin"],
          "sameAs": [
            "https://www.linkedin.com/in/lily-lai-03382a87/",
            "https://luriegroup.com/agent/lily-lai",
            "https://cityrealestatesf.com/",
            "https://share.google/1tH7mPayVavdCtOYg",
            "https://www.instagram.com/lily_lai__/",
            "https://www2.dre.ca.gov/PublicASP/pplinfo.asp?License_id=02123349"
          ],
          "hasCredential": {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "professional-certificate",
            "name": "California DRE License #02123349",
            "recognizedBy": {
              "@type": "Organization",
              "name": "California Department of Real Estate"
            }
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "629 Divisadero St",
            "addressLocality": "San Francisco",
            "addressRegion": "CA",
            "postalCode": "94117",
            "addressCountry": "US"
          }
        })}</script>
      </Helmet>

      <div className="about-page">

        {/* Bio */}
        <div className="about-bio">
          <Grid stackable stretched centered>
            <Grid.Column computer={5} tablet={5} mobile={12} verticalAlign="middle">
              <FadeInSection>
                <div className="about-portrait-wrapper">
                  <Image src={lily} className="about-portrait" alt="Lily Lai" title="Lily Lai" />
                </div>
              </FadeInSection>
            </Grid.Column>
            <Grid.Column computer={7} tablet={9} mobile={12} verticalAlign="middle">
              <FadeInSection>
                <p className="about-eyebrow">Bay Area Realtor</p>
                <h1 className="about-heading">About Lily</h1>
                <div className="about-body">
                  <p>Lily Lai is a native San Franciscan and top Bay Area Realtor with over $100 million in closed sales and 100+ satisfied clients. Ranked in the top 5% of San Francisco agents, she specializes in residential real estate across San Francisco, South San Francisco, Pacifica, and the Peninsula.</p>
                  <p>As a trusted advisor, Lily guides buyers and sellers through every stage of the transaction — from strategic pricing and staging to offer negotiation and closing. Her deep knowledge of San Francisco neighborhoods and the broader Bay Area market helps clients make confident, well-informed decisions.</p>
                  <p>Years of hands-on renovation work alongside her father give Lily a rare edge: she can assess a home's true potential, identify value-add opportunities, and estimate real construction costs — insight most real estate agents simply don't have.</p>
                  <p>Lily is a Cantonese and Mandarin-speaking Realtor proudly serving the Chinese-speaking community across the Bay Area. Whether you're buying your first home, selling a property, or navigating a relocation, she brings the same dedication, clarity, and care to every client. 粤語及普通話服務。</p>
                  <p className="about-languages"><span>Languages:</span> English · 粤語 (Cantonese) · 普通話 (Mandarin)</p>
                </div>
              </FadeInSection>
            </Grid.Column>
          </Grid>
        </div>

        {/* Video */}
        <div className="about-video-section">
          <FadeInSection>
            <h2 className="about-section-heading">Get To Know Me</h2>
            <div className="about-video">
              <ReactPlayer
                url="https://youtu.be/jykV1NOn5oE"
                className="react-player"
                playing={false}
                width="100%"
                height="100%"
                controls={true}
              />
            </div>
          </FadeInSection>
        </div>

        {/* Affiliations */}
        <div className="about-affiliations">
          <FadeInSection>
            <p className="about-eyebrow" style={{ textAlign: 'center' }}>Credentials & Affiliations</p>
            <h2 className="about-affiliations-heading">Professional Memberships</h2>
          </FadeInSection>
          <div className="affiliations-inner">
            <FadeInSection>
              <ul className="affiliations-list">
                <li>SFAR Global Business Council — 2026 Chair</li>
                <li>SFAR Global Business Council — 2025 Co-Chair</li>
                <li>Women's Council of Realtors San Francisco — 2024 President</li>
                <li>Women's Council of Realtors San Francisco — 2023 First Vice President</li>
                <li>Compass Rising Star — 2023</li>
                <li>National Association of Realtors — Member</li>
                <li>California Association of Realtors — Member</li>
                <li>San Francisco Association of Realtors — Member</li>
                <li>National Association of Realtors Green Designation</li>
              </ul>
            </FadeInSection>
            <motion.div
              className="affiliation-logos"
              variants={logosContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
            >
              {[
                { src: car, alt: 'California Association of Realtors' },
                { src: sfar, alt: 'San Francisco Association of Realtors' },
                { src: wcr, alt: "Women's Council of Realtors" },
                { src: green, alt: 'NAR Green Designation' },
                { src: rs, alt: 'Residential Specialist' },
                { src: gold, alt: 'Gold' },
              ].map(({ src, alt }) => (
                <motion.div className="affiliation-logo-item" key={alt} variants={logoItem}>
                  <Image src={src} alt={alt} title={alt} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

      </div>
    </>
  );
}

export default About;
